import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Btn, Empty } from "@/components/common";
import { Building2, Plus, MonitorSmartphone, BellRing, X, Cloud } from "lucide-react";
import { toast } from "sonner";

const DIR_SOURCE = {
  entra: { label: "Entra", cls: "bg-cyan-950/50 text-cyan-300 border-cyan-800/60" },
  google: { label: "Google", cls: "bg-sky-950/50 text-sky-300 border-sky-800/60" },
  csv: { label: "CSV", cls: "bg-amber-950/50 text-amber-300 border-amber-800/60" },
  manual: { label: "Manual", cls: "bg-slate-800 text-slate-400 border-slate-700" },
};

export default function Customers() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");

  const load = () => api.get("/customers").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/customers", { name, notes });
      toast.success("Customer created");
      setShow(false); setName(""); setNotes(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!rows) return <div className="p-6"><Spinner /></div>;
  return (
    <div className="p-6 fade-up">
      <PageHeader title="Customers" subtitle={`${rows.length} managed organizations`} testid="customers-page"
        actions={can("customers:create") && <Btn data-testid="add-customer-btn" onClick={() => setShow(true)}><Plus size={15} /> New Customer</Btn>} />

      {rows.length === 0 ? <Empty icon={Building2} title="No customers yet" hint="Create your first customer to start enrolling devices." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((c) => (
            <div key={c.id} data-testid="customer-card" onClick={() => nav("/customers/" + c.id)}
              className="card-surface rounded-lg p-4 cursor-pointer hover:bg-slate-800/40 hover:border-cyan-800 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-md bg-cyan-500/10 flex items-center justify-center"><Building2 size={18} className="text-cyan-400" /></div>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-slate-100 truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500">{c.site_count} sites</div>
                </div>
                {c.directory_source && (
                  <span data-testid="customer-dir-source" title={`People managed via ${DIR_SOURCE[c.directory_source]?.label || c.directory_source}`}
                    className={"ml-auto inline-flex items-center gap-1 text-[10px] rounded-full border px-2 py-0.5 " + (DIR_SOURCE[c.directory_source]?.cls || DIR_SOURCE.manual.cls)}>
                    <Cloud size={10} /> {DIR_SOURCE[c.directory_source]?.label || c.directory_source}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><div className="font-mono text-lg text-slate-200">{c.device_count}</div><div className="text-[10px] text-slate-500 flex items-center justify-center gap-1"><MonitorSmartphone size={10} /> devices</div></div>
                <div><div className="font-mono text-lg text-emerald-400">{c.online_count}</div><div className="text-[10px] text-slate-500">online</div></div>
                <div><div className={"font-mono text-lg " + (c.open_alerts ? "text-rose-400" : "text-slate-400")}>{c.open_alerts}</div><div className="text-[10px] text-slate-500 flex items-center justify-center gap-1"><BellRing size={10} /> alerts</div></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShow(false)}>
          <div className="card-surface rounded-xl p-5 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-slate-100">New Customer</h2>
              <button onClick={() => setShow(false)} className="text-slate-500 hover:text-slate-200"><X size={18} /></button>
            </div>
            <label className="text-[12px] text-slate-400">Customer name</label>
            <input data-testid="customer-name-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus
              className="mt-1 mb-3 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-cyan-500" />
            <label className="text-[12px] text-slate-400">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="mt-1 mb-4 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-cyan-500" />
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setShow(false)}>Cancel</Btn>
              <Btn data-testid="customer-save-btn" onClick={create} disabled={!name}>Create</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
